/**
 * ARMORE Deluxe Ring purchase-state controller (V11.1).
 *
 * Scope: forms marked [data-armore-deluxe-ring="true"] only
 *        (Liquid: product.handle == 'deluxe-ring').
 * Owns: color selection, Advanced Size purchase gate, exact variant id,
 *       native Size-wrapper visibility (parity with ArmoreProductOptions).
 * Pattern: working backup syncAndValidateForm / document swatch handling
 *          (theme 208781115731) — without dual controllers.
 *
 * ArmoreProductOptions must skip these forms (see armore-product-options.js).
 * ProductForm must not write name=id on these forms (see theme.js gate).
 * ProductForm.destroy / section:unload must call ArmoreDeluxeRing.destroy.
 */
(function () {
  'use strict';
  var rootWindow = typeof window !== 'undefined' ? window : null;
  var rootDocument = typeof document !== 'undefined' ? document : null;
  if (!rootWindow || !rootDocument) return;

  var FORM_SEL = 'form[data-armore-deluxe-ring="true"]';
  var instances = new WeakMap();
  var ATC_LABEL = (rootWindow.theme && rootWindow.theme.strings && rootWindow.theme.strings.addToCart) || 'Add to cart';
  var SOLD_OUT = (rootWindow.theme && rootWindow.theme.strings && rootWindow.theme.strings.soldOut) || 'Sold out';

  function normalizeValue(str) {
    return String(str || '')
      .trim()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/\s+/g, ' ');
  }

  function readProductJSON(form) {
    var section = form.closest('[id^="shopify-section-"]') || form.closest('[data-section-id]') || rootDocument;
    var node = (section && section.querySelector('[data-product-json]')) || rootDocument.querySelector('[data-product-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || 'null');
    } catch (_) {
      return null;
    }
  }

  function eventElement(event) {
    var t = event && event.target;
    if (!t) return null;
    if (typeof Element !== 'undefined' && t instanceof Element) return t;
    if (t.nodeType === 3 && t.parentElement) return t.parentElement;
    return typeof t.closest === 'function' ? t : null;
  }

  function getSelectedOptions(form, product) {
    var optionCount = (product && product.options && product.options.length) || 2;
    var ordered = [];
    var i;
    for (i = 0; i < optionCount; i++) {
      var val = null;
      var advRootEl = form.querySelector('[data-advsz]');
      if (advRootEl && parseInt(advRootEl.getAttribute('data-opt-index') || '-1', 10) === i) {
        if (advRootEl.getAttribute('data-selected') === 'true') {
          var selectedBtn = advRootEl.querySelector('.advsz__option.is-selected');
          if (selectedBtn) val = selectedBtn.getAttribute('data-option-value');
        }
      }
      if (!val) {
        var radio = form.querySelector(
          '[data-single-option-selector][data-index="option' + (i + 1) + '"]:checked'
        );
        if (radio) val = radio.value;
      }
      if (!val) {
        var swatchWrapper = form.querySelector('#swatch-option' + (i + 1)) ||
          form.querySelector('[id^="swatch-option' + (i + 1) + '"]');
        if (swatchWrapper) {
          var selectedSwatch = swatchWrapper.querySelector('.swatch-view-item[aria-checked="true"]');
          if (selectedSwatch) {
            val =
              selectedSwatch.getAttribute('data-value') ||
              selectedSwatch.getAttribute('data-option-value') ||
              selectedSwatch.getAttribute('orig-value');
          }
        }
      }
      ordered[i] = val ? String(val).trim() : null;
    }
    return ordered;
  }

  function findExactVariant(product, orderedOptions) {
    if (!product || !orderedOptions || !product.options) return null;
    if (orderedOptions.length !== product.options.length || !orderedOptions.every(Boolean)) return null;
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var opts = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
      var match = opts.every(function (optVal, idx) {
        return normalizeValue(optVal) === normalizeValue(orderedOptions[idx]);
      });
      if (match) return variant;
    }
    return null;
  }

  function findDisplayVariant(product, orderedOptions) {
    if (!product) return null;
    var variants = product.variants || [];
    if (orderedOptions && orderedOptions.some(Boolean)) {
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var opts = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
        var match = opts.every(function (optVal, idx) {
          return !orderedOptions[idx] || normalizeValue(optVal) === normalizeValue(orderedOptions[idx]);
        });
        if (match) return variant;
      }
    }
    return variants[0] || null;
  }

  function applySwatchVisual(form, colorValue) {
    var items = form.querySelectorAll('.swatch-view-item');
    items.forEach(function (item) {
      var itemVal =
        item.getAttribute('data-value') ||
        item.getAttribute('data-option-value') ||
        item.getAttribute('orig-value');
      var selected = normalizeValue(itemVal) === normalizeValue(colorValue);
      item.setAttribute('aria-checked', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
      var sel = item.querySelector('.swatch-selector');
      if (sel) sel.classList.toggle('swatch-selected', selected);
    });
  }

  function setColorRadio(form, colorValue, dispatch) {
    var radios = form.querySelectorAll('[data-single-option-selector][data-index="option1"], input[name="options[Color]"]');
    var matched = null;
    radios.forEach(function (radio) {
      if (normalizeValue(radio.value) === normalizeValue(colorValue)) {
        radio.checked = true;
        matched = radio;
      }
    });
    applySwatchVisual(form, colorValue);
    if (matched && dispatch) {
      matched.dispatchEvent(new Event('input', { bubbles: true }));
      matched.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return !!matched;
  }

  function syncAndValidateForm(form, product, committedColor) {
    if (committedColor) {
      var current = form.querySelector('input[name="options[Color]"]:checked');
      if (!current || normalizeValue(current.value) !== normalizeValue(committedColor)) {
        setColorRadio(form, committedColor, false);
      } else {
        applySwatchVisual(form, committedColor);
      }
    }

    var advRootEl = form.querySelector('[data-advsz]');
    var isSizeChosen = !advRootEl || advRootEl.getAttribute('data-selected') === 'true';
    var selected = getSelectedOptions(form, product);
    if (committedColor) selected[0] = committedColor;

    var exactMatched = isSizeChosen ? findExactVariant(product, selected) : null;
    var displayVariant = exactMatched || findDisplayVariant(product, selected);
    var idInput = form.querySelector('input[name="id"], [data-armore-variant-id]');
    var paymentEl = form.querySelector('[data-armore-payment]');
    var atcBtn = form.querySelector('button[name="add"][data-add-to-cart], [data-add-to-cart]');
    var atcText = atcBtn ? atcBtn.querySelector('[data-add-to-cart-text]') : null;
    var unselectedLabel =
      (advRootEl && (advRootEl.getAttribute('data-unselected-label') || advRootEl.dataset.unselectedLabel)) ||
      'SELECT SIZE';

    if (!isSizeChosen) {
      if (idInput) {
        idInput.value = '';
        idInput.disabled = true;
      }
      if (paymentEl) paymentEl.hidden = true;
      if (atcBtn) {
        atcBtn.disabled = false;
        if (atcText) atcText.textContent = unselectedLabel;
      }
    } else if (exactMatched) {
      if (idInput) {
        idInput.disabled = false;
        idInput.value = String(exactMatched.id);
      }
      if (atcBtn) {
        atcBtn.disabled = !exactMatched.available;
        if (atcText) atcText.textContent = exactMatched.available ? ATC_LABEL : SOLD_OUT;
      }
      if (paymentEl) paymentEl.hidden = !exactMatched.available;
    } else {
      if (idInput) {
        idInput.value = '';
        idInput.disabled = true;
      }
      if (atcBtn) {
        atcBtn.disabled = true;
        if (atcText) atcText.textContent = SOLD_OUT;
      }
      if (paymentEl) paymentEl.hidden = true;
    }

    if (displayVariant) {
      try {
        rootDocument.dispatchEvent(
          new CustomEvent('theme:variant:change', {
            detail: { variant: displayVariant, armoreDeluxeRing: true },
            bubbles: true,
          })
        );
      } catch (_) {}
    }

    return { exactMatched: exactMatched, displayVariant: displayVariant, selected: selected, isSizeChosen: isSizeChosen };
  }

  function bindForm(form) {
    if (!form || instances.has(form)) return;
    var product = readProductJSON(form);
    if (!product || !product.variants) {
      if (typeof console !== 'undefined') {
        console.warn('[ArmoreDeluxeRing] missing product JSON — controller inactive for form', form.id);
      }
      return;
    }

    form.setAttribute('data-armore-purchase-authority', 'deluxe-ring');
    var committedColor =
      (form.querySelector('input[name="options[Color]"]:checked') || {}).value || 'Silver';
    var advanced = form.querySelector('[data-advsz]');
    var cleanups = [];
    var guard = false;

    function listen(target, type, handler, opts) {
      target.addEventListener(type, handler, opts || false);
      cleanups.push(function () {
        target.removeEventListener(type, handler, opts || false);
      });
    }

    function sync() {
      if (guard) return;
      guard = true;
      try {
        syncAndValidateForm(form, product, committedColor);
      } finally {
        guard = false;
      }
    }

    function chooseColor(value, fromUser) {
      if (!value) return;
      if (fromUser) committedColor = value;
      setColorRadio(form, value, true);
      sync();
    }

    function chooseSwatch(item) {
      if (!item || item.closest('form') !== form) return;
      if (item.closest('.advsz')) return;
      var val =
        item.getAttribute('data-value') ||
        item.getAttribute('data-option-value') ||
        item.getAttribute('orig-value');
      var radio = item.querySelector('input[data-single-option-selector]');
      if (radio && radio.disabled) return;
      if (item.getAttribute('aria-disabled') === 'true') return;
      if (!val && radio) val = radio.value;
      chooseColor(val, true);
    }

    // --- Document-level swatch handling (backup parity), section-scoped ---
    var section = form.closest('[id^="shopify-section-"]') || form;
    listen(
      rootDocument,
      'click',
      function (e) {
        var el = eventElement(e);
        if (!el) return;
        if (!section.contains(el)) return;
        var item = el.closest('.swatch-view-item');
        if (!item || item.closest('form') !== form) return;
        if (el.tagName === 'INPUT') return;
        e.preventDefault();
        chooseSwatch(item);
      },
      true
    );

    // Defend committed color against foreign writers (Broadcast / apps / duplicate handlers)
    listen(form, 'change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (t.name.indexOf('options[Color]') === 0 || (t.getAttribute('data-index') === 'option1' && t.type === 'radio')) {
        if (committedColor && normalizeValue(t.value) !== normalizeValue(committedColor) && t.checked) {
          // Foreign writer flipped color — restore user commitment
          setColorRadio(form, committedColor, false);
        }
      }
      sync();
    });

    var destroyed = false;
    function destroyInstance() {
      if (destroyed) return;
      destroyed = true;
      while (cleanups.length) {
        try {
          cleanups.pop()();
        } catch (_) {}
      }
      instances.delete(form);
    }

    // Keep 100ms protect until iPhone proves event-only defense is equivalent (V11 color non-regression).
    var protectTimer = rootWindow.setInterval(function () {
      if (!form.isConnected) {
        destroyInstance();
        return;
      }
      var checked = form.querySelector('input[name="options[Color]"]:checked');
      if (committedColor && (!checked || normalizeValue(checked.value) !== normalizeValue(committedColor))) {
        setColorRadio(form, committedColor, false);
        applySwatchVisual(form, committedColor);
      }
    }, 100);
    cleanups.push(function () {
      clearInterval(protectTimer);
    });

    // --- Advanced Size (UI + gate); purchase id owned here ---
    var nativeSizeWrapper = null;
    if (advanced) {
      var optIndex = Number(advanced.dataset.optIndex || advanced.getAttribute('data-opt-index') || 1);
      var sizeIndexAttr = 'option' + (optIndex + 1);
      var sizeInputs = function () {
        return Array.from(form.querySelectorAll('[data-single-option-selector]')).filter(function (input) {
          return input.dataset.index === sizeIndexAttr || input.getAttribute('data-index') === sizeIndexAttr;
        });
      };
      var overlay = advanced.querySelector('.advsz__overlay');
      var modal = advanced.querySelector('.advsz__modal');
      var trigger = advanced.querySelector('.advsz__trigger');
      var triggerText = advanced.querySelector('.advsz__trigger-text');
      var back = advanced.querySelector('.advsz__back');
      var list = advanced.querySelector('[data-panel="list"]');
      var menu = advanced.querySelector('[data-panel="guide-menu"]');
      var guide = advanced.querySelector('[data-panel="guide"]');
      var slot = advanced.querySelector('[data-guide-slot]');
      var title = advanced.querySelector('.advsz__title');
      var buttons = Array.from(advanced.querySelectorAll('.advsz__option'));
      var error = form.querySelector('[data-armore-size-error]');
      var opened = false;
      var previousOverflow = '';
      var guideView = 'list';
      var guideHydrated = false;

      var missing = [];
      if (!trigger) missing.push('.advsz__trigger');
      if (!triggerText) missing.push('.advsz__trigger-text');
      if (!overlay) missing.push('.advsz__overlay');
      if (!modal) missing.push('.advsz__modal');
      if (!title) missing.push('.advsz__title');

      if (missing.length) {
        // Fail open: keep native Size visible so shopper can still choose a size.
        if (typeof console !== 'undefined') {
          console.warn('[ArmoreDeluxeRing] Adv Size incomplete — leaving native Size visible:', missing.join(', '));
        }
      } else {
        // Hide only the native Size wrapper for THIS form (parity with ArmoreProductOptions).
        var firstSize = sizeInputs()[0];
        nativeSizeWrapper = firstSize ? firstSize.closest('.selector-wrapper') : null;
        if (nativeSizeWrapper && form.contains(nativeSizeWrapper) && !nativeSizeWrapper.querySelector('.swatch-view-item')) {
          nativeSizeWrapper.hidden = true;
          nativeSizeWrapper.setAttribute('aria-hidden', 'true');
          nativeSizeWrapper.setAttribute('data-armore-native-size-hidden', 'true');
          sizeInputs().forEach(function (input) {
            input.setAttribute('tabindex', '-1');
          });
          cleanups.push(function () {
            if (!nativeSizeWrapper) return;
            nativeSizeWrapper.hidden = false;
            nativeSizeWrapper.removeAttribute('aria-hidden');
            nativeSizeWrapper.removeAttribute('data-armore-native-size-hidden');
            sizeInputs().forEach(function (input) {
              input.removeAttribute('tabindex');
            });
          });
        }
      }

      // Backup parity: size radios must not stay SSR-checked while Adv Size unselected
      if (advanced.getAttribute('data-selected') !== 'true') {
        sizeInputs().forEach(function (r) {
          r.checked = false;
        });
      }

      function hideAllPanels() {
        if (list) list.hidden = true;
        if (menu) menu.hidden = true;
        if (guide) guide.hidden = true;
      }

      function showList() {
        hideAllPanels();
        if (list) list.hidden = false;
        if (back) back.classList.add('advsz__back--hidden');
        if (title) title.textContent = advanced.dataset.chooseTitle || 'Select Size';
        guideView = 'list';
        if (modal) modal.setAttribute('data-view', 'list');
      }

      function showMenu() {
        hideAllPanels();
        if (menu) menu.hidden = false;
        else if (guide) guide.hidden = false;
        if (back) back.classList.remove('advsz__back--hidden');
        if (title) title.textContent = 'SIZE GUIDE';
        guideView = 'menu';
        if (modal) modal.setAttribute('data-view', 'menu');
      }

      function hydrateGuide() {
        if (guideHydrated || !slot) return;
        var preferred = advanced.dataset.guideSection;
        var source =
          (preferred && rootDocument.getElementById('shopify-section-' + preferred)) ||
          (form.closest('[data-section-id]') && form.closest('[data-section-id]').querySelector('[data-advsg="root"]')) ||
          rootDocument.querySelector('[data-advsg="root"]');
        var content = source && source.querySelector('[data-advsg-wrap]');
        if (content) {
          slot.innerHTML = content.innerHTML;
          guideHydrated = true;
        }
      }

      function showGuideDetail() {
        hideAllPanels();
        if (guide) guide.hidden = false;
        if (back) back.classList.remove('advsz__back--hidden');
        if (title) title.textContent = 'SIZE GUIDE';
        guideView = 'detail';
        if (modal) modal.setAttribute('data-view', 'detail');
        hydrateGuide();
      }

      function closeOverlay() {
        if (!overlay || !modal) return;
        overlay.classList.remove('is-open');
        overlay.hidden = true;
        modal.hidden = true;
        if (opened) rootDocument.documentElement.style.overflow = previousOverflow;
        opened = false;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }

      function openOverlay() {
        if (!overlay || !modal || !trigger) return;
        if (!opened) previousOverflow = rootDocument.documentElement.style.overflow;
        opened = true;
        showList();
        overlay.hidden = false;
        modal.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        rootDocument.documentElement.style.overflow = 'hidden';
        rootWindow.requestAnimationFrame(function () {
          overlay.classList.add('is-open');
          if (modal.focus) modal.focus();
        });
      }

      function selectSize(value) {
        var input = sizeInputs().find(function (i) {
          return normalizeValue(i.value) === normalizeValue(value);
        });
        if (!input) return;
        advanced.dataset.selected = 'true';
        advanced.setAttribute('data-selected', 'true');
        sizeInputs().forEach(function (r) {
          r.checked = normalizeValue(r.value) === normalizeValue(value);
        });
        buttons.forEach(function (btn) {
          var selected = normalizeValue(btn.getAttribute('data-option-value')) === normalizeValue(value);
          btn.classList.toggle('is-selected', selected);
          btn.setAttribute('aria-pressed', String(selected));
        });
        if (triggerText) {
          var label = advanced.dataset.sizeLabel || 'SIZE';
          triggerText.textContent = label + ' ' + value;
        }
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        closeOverlay();
        sync();
      }

      if (!missing.length) {
        if (trigger) listen(trigger, 'click', openOverlay);
        if (overlay) {
          listen(overlay, 'click', function (e) {
            if (e.target === overlay) closeOverlay();
          });
        }
        var closeBtn = advanced.querySelector('.advsz__close');
        if (closeBtn) listen(closeBtn, 'click', closeOverlay);
        if (back) {
          listen(back, 'click', function () {
            if (guideView === 'detail' && menu) showMenu();
            else showList();
          });
        }
        if (modal) {
          listen(modal, 'keydown', function (event) {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              closeOverlay();
            }
          });
        }
        var guideButton = advanced.querySelector('[data-open-guide]');
        if (guideButton) {
          listen(guideButton, 'click', function () {
            if (menu) showMenu();
            else showGuideDetail();
          });
        }
        if (menu) {
          menu.querySelectorAll('[data-sg-open], [data-sg-open-category]').forEach(function (row) {
            listen(row, 'click', function () {
              showGuideDetail();
            });
          });
        }
        buttons.forEach(function (button) {
          listen(button, 'click', function () {
            selectSize(button.getAttribute('data-option-value'));
          });
        });

        listen(
          form,
          'click',
          function (e) {
            var el = eventElement(e);
            if (!el || !el.closest('[data-add-to-cart], .shopify-payment-button, [data-armore-payment]')) return;
            if (advanced.getAttribute('data-selected') === 'true') return;
            e.preventDefault();
            e.stopImmediatePropagation();
            var idInput = form.querySelector('input[name="id"]');
            if (idInput) {
              idInput.value = '';
              idInput.disabled = true;
            }
            if (error) {
              error.textContent = advanced.dataset.requiredMessage || 'Select a size';
              error.hidden = false;
            }
            openOverlay();
          },
          true
        );

        listen(
          form,
          'submit',
          function (e) {
            if (advanced.getAttribute('data-selected') === 'true') {
              var selected = getSelectedOptions(form, product);
              if (committedColor) selected[0] = committedColor;
              var matched = findExactVariant(product, selected);
              if (!matched || !matched.available) {
                e.preventDefault();
                e.stopImmediatePropagation();
                return;
              }
              var idInput = form.querySelector('input[name="id"]');
              if (idInput) {
                idInput.disabled = false;
                idInput.value = String(matched.id);
              }
              return;
            }
            e.preventDefault();
            e.stopImmediatePropagation();
            openOverlay();
          },
          true
        );
      }
    }

    // Boot
    applySwatchVisual(form, committedColor);
    sync();

    var api = {
      chooseColor: function (v) {
        chooseColor(v, true);
      },
      sync: sync,
      getCommittedColor: function () {
        return committedColor;
      },
      destroy: destroyInstance,
    };
    instances.set(form, api);
    return api;
  }

  function boot(root) {
    var scope = root || rootDocument;
    scope.querySelectorAll(FORM_SEL).forEach(bindForm);
  }

  function destroyInRoot(root) {
    var scope = root || rootDocument;
    scope.querySelectorAll(FORM_SEL).forEach(function (form) {
      var inst = instances.get(form);
      if (inst) inst.destroy();
    });
  }

  rootWindow.ArmoreDeluxeRing = {
    bindForm: bindForm,
    boot: boot,
    destroy: function (form) {
      var inst = instances.get(form);
      if (inst) inst.destroy();
    },
    destroyInRoot: destroyInRoot,
    // Test helpers
    _findExactVariant: findExactVariant,
    _syncAndValidateForm: syncAndValidateForm,
    _normalizeValue: normalizeValue,
  };

  if (rootDocument.readyState === 'loading') {
    rootDocument.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(rootDocument);
      },
      { once: true }
    );
  } else {
    boot(rootDocument);
  }
  rootDocument.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
  rootDocument.addEventListener('shopify:section:unload', function (event) {
    destroyInRoot(event.target);
  });
})();
